const global_comments_utils = {
    FETCH_FREQUENCY_MS: 200,
    KEYCODE_ENTER: 13,
    COMMENT_MAX_LENGTH: 2500,
    ALLOWED_KEYCODES: [
        8, // BACKSPACE
        35, // END
        36, // HOME
        37, // LEFT
        38, // UP
        39, // RIGHT
        40, // DOWN
        46 // DEL
    ],
    COMMENTS_TAB_ID: 'comments_tab',
    ANIMATION_SPEED: 200,
    INCLUDE_TARGET_TYPES: {
        ANNOUNCEMENT: 'announcement',
        SUBMISSION: 'submission',
        TASK: 'task'
    },

    /**
     * Make any corrections to the <span>@Mention</span>s that may have been corrupted by user input. For a detailed
     * explanation, see below:
     *
     *
     * Originally we were handling this by using a <span contenteditable="false"> inside of the larger <p contenteditable="true">.
     * However, there's a bug on Android when using the software keyboard where pressing backspace right after a
     * <span contenteditable="false"> spazzes out and keeps duplicating the metion:
     *     - @see @jira:NOTIF-165
     *     - @see https://bugs.chromium.org/p/chromium/issues/detail?id=612446
     *     - @see https://github.com/ProseMirror/prosemirror/issues/565
     *
     * Android also has a bug where the keycode in a contenteditable="true" is always 229, so we can't override the backspace
     * key to address this (@see https://stackoverflow.com/q/36753548/477632).
     *
     * The solution is to write a data-check-text value into each mention <span> and check it here against the actual
     * display value of the mention. If they don't match, we'll do some DOM manipulation to correct things (either
     * delete the mention altogether or separate unrelated text from it).
     *
     * This has the added benefit of working better on IE and being a little less awkward when trying to insert the cursor
     * in the middle of a mention, so we're using this workaround universally on web and mobile.
     */
    fix_corrupted_mention_html: function ($comment_creator) {
        var this_ = this;

        $comment_creator.find('span').each(function (i, span) {
            var $span = $(span);
            var check_text = $span.attr('data-check-text');
            var span_text = $span.text();

            if (this_._is_mention_html_valid($span)) {
                return;
            }

            // Fix case where user starts typing directly after mention ends (break new text out of mention and add it after)
            if (span_text.indexOf(check_text) === 0 && check_text !== span_text) {
                this_._fix_mention_suffix_text($span, $comment_creator);
                return;
            }

            // Fix case where user has started modifying/deleting the mention - we'll delete it
            if (check_text !== span_text) {
                $span.remove();
            }
        });

        // IE will insert a <font> tag after deleting a $span, so we should clean these up
        $comment_creator.find('font').remove();
    },

    _is_mention_html_valid: function ($span) {
        if (!$span.text()) {
            return;
        }

        return $span.attr('data-check-text') === $span.text();
    },

    /**
     * This corrects a mention <span> in the CommentCreator after a user has manually added text to it and that text
     * was inserted into the <span>mention</span>, rather than after it.
     *
     * For example:
     * `<span data-check-text="@Testy McGee">@Testy McGeea</span>` --> `<span data-check-text="@Testy McGee">@Testy McGee</span> a`
     *
     * @param {*} $span
     * @param {*} $comment_creator
     */
    _fix_mention_suffix_text: function ($span, $comment_creator) {
        var check_text = $span.attr('data-check-text');
        var span_text = $span.text();

        var remainder = span_text.split(check_text)[1];
        $span.html(check_text);
        $span.after(remainder);
        global_comments_utils.move_cursor_to_end_of_contenteditable($comment_creator);
        return;
    },

    /**
     * Moves the cursor to the end of the contenteditable div.
     *
     * @param {jQuery Element} $el The contenteditable element
     */
    move_cursor_to_end_of_contenteditable: function ($comment_creator) {
        var el = $comment_creator.get(0);
        var range = document.createRange();
        var sel = window.getSelection();
        var node = el.childNodes[el.childNodes.length - 1]; // last node

        range.setStart(node, node.length);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    },

    /**
     * Removes formatting and HTML tags from clipboard when pasting.
     *
     * @see https://stackoverflow.com/a/12028136/477632
     */
    sanitize_comment_pasted_html: function (event) {
        // cancel paste
        event.preventDefault();

        // get text representation of clipboard
        var text = null;
        if (window.clipboardData && window.clipboardData.getData) {
            // IE
            text = window.clipboardData.getData('Text');
        } else {
            text = (event.originalEvent || event).clipboardData.getData('text/plain');
        }

        // insert text manually
        // @note: @author:stas This method (document.execCommand) is obsolete and should not be used.
        // In particular, to interact with the clipboard, consider using the Clipboard API instead.
        document.execCommand(navigator.userAgent.indexOf('Trident') !== -1 ? 'paste' : 'insertHTML', false, text);
    }
};

/**
 * This is ported from Rangy's selection save and restore module and has no dependencies.
 * Copyright 2019, Tim Down
 * Licensed under the MIT license.
 *
 * Documentation: https://github.com/timdown/rangy/wiki/Selection-Save-Restore-Module
 * Use "rangeSelectionSaveRestore" instead of "rangy"
 * (credits : https://gist.github.com/timdown/244ae2ea7302e26ba932a43cb0ca3908)
 */
global_comments_utils.rangeSelectionSaveRestore = (function () {
    var markerTextChar = '\ufeff';
    var selectionHasExtend = typeof window.getSelection().extend !== 'undefined';

    function gEBI(id, doc) {
        return (doc || document).getElementById(id);
    }

    function removeNode(node) {
        node.parentNode.removeChild(node);
    }

    // Utility function to support direction parameters in the API that may be a string ("backward", "backwards",
    // "forward" or "forwards") or a Boolean (true for backwards).
    function isDirectionBackward(dir) {
        return typeof dir == 'string' ? /^backward(?:s)?$/i.test(dir) : !!dir;
    }

    function isSelectionBackward(sel) {
        var backward = false;
        if (!sel.isCollapsed) {
            var range = document.createRange();
            range.setStart(sel.anchorNode, sel.anchorOffset);
            range.setEnd(sel.focusNode, sel.focusOffset);
            backward = range.collapsed;
        }
        return backward;
    }

    function selectRangeBackwards(sel, range) {
        if (selectionHasExtend) {
            var endRange = range.cloneRange();
            endRange.collapse(false);
            sel.removeAllRanges();
            sel.addRange(endRange);
            sel.extend(range.startContainer, range.startOffset);
            return true;
        } else {
            // Just select the range forwards
            sel.removeAllRanges();
            sel.addRange(range);
            return false;
        }
    }

    function insertRangeBoundaryMarker(range, atStart) {
        var markerId = 'selectionBoundary_' + +new Date() + '_' + ('' + Math.random()).slice(2);
        var markerEl;
        var doc = range.startContainer.ownerDocument;

        // Clone the Range and collapse to the appropriate boundary point
        var boundaryRange = range.cloneRange();
        boundaryRange.collapse(atStart);

        // Create the marker element containing a single invisible character using DOM methods and insert it
        markerEl = doc.createElement('span');
        markerEl.id = markerId;
        markerEl.style.lineHeight = '0';
        markerEl.style.display = 'none';
        markerEl.textContent = markerTextChar;

        boundaryRange.insertNode(markerEl);
        return markerEl;
    }

    function setRangeBoundary(doc, range, markerId, atStart) {
        var markerEl = gEBI(markerId, doc);
        if (markerEl) {
            range[atStart ? 'setStartBefore' : 'setEndBefore'](markerEl);
            removeNode(markerEl);
        } else {
            console.warn('Marker element has been removed. Cannot restore selection.');
        }
    }

    function compareRanges(r1, r2) {
        return r2.compareBoundaryPoints(r1.START_TO_START, r1);
    }

    function saveRange(range, direction) {
        var startEl,
            endEl,
            doc = range.startContainer.ownerDocument,
            text = range.toString();

        if (range.collapsed) {
            endEl = insertRangeBoundaryMarker(range, false);
            return {
                document: doc,
                markerId: endEl.id,
                collapsed: true
            };
        } else {
            endEl = insertRangeBoundaryMarker(range, false);
            startEl = insertRangeBoundaryMarker(range, true);

            return {
                document: doc,
                startMarkerId: startEl.id,
                endMarkerId: endEl.id,
                collapsed: false,
                backward: isDirectionBackward(direction),
                toString: function () {
                    return "original text: '" + text + "', new text: '" + range.toString() + "'";
                }
            };
        }
    }

    function restoreRange(rangeInfo) {
        var doc = rangeInfo.document;
        var range = doc.createRange(doc);
        if (rangeInfo.collapsed) {
            var markerEl = gEBI(rangeInfo.markerId, doc);
            if (markerEl) {
                markerEl.style.display = 'inline';
                var previousNode = markerEl.previousSibling;

                if (previousNode && previousNode.nodeType == 3) {
                    removeNode(markerEl);
                    range.setStart(previousNode, previousNode.length);
                    range.collapse(true);
                } else {
                    range.setEndBefore(markerEl);
                    range.collapse(false);
                    removeNode(markerEl);
                }
            } else {
                console.warn('Marker element has been removed. Cannot restore selection.');
            }
        } else {
            setRangeBoundary(doc, range, rangeInfo.startMarkerId, true);
            setRangeBoundary(doc, range, rangeInfo.endMarkerId, false);
        }

        return range;
    }

    function saveRanges(ranges, direction) {
        // Order the ranges by position within the DOM, latest first, cloning the array to leave the original untouched
        ranges = ranges.slice(0);
        ranges.sort(compareRanges);
        var backward = isDirectionBackward(direction);

        var rangeInfos = ranges.map(function (range) {
            return saveRange(range, backward);
        });

        // Now that all the markers are in place and DOM manipulation is over, adjust each range's boundaries to lie
        // between its markers
        for (var i = ranges.length - 1, range, doc; i >= 0; --i) {
            range = ranges[i];
            doc = range.startContainer.ownerDocument;
            if (range.collapsed) {
                range.setStartAfter(gEBI(rangeInfos[i].markerId, doc));
                range.collapse(true);
            } else {
                range.setEndBefore(gEBI(rangeInfos[i].endMarkerId, doc));
                range.setStartAfter(gEBI(rangeInfos[i].startMarkerId, doc));
            }
        }

        return rangeInfos;
    }

    function saveSelection(win) {
        win = win || window;
        var sel = win.getSelection();
        var ranges = [];
        for (var i = 0; i < sel.rangeCount; ++i) {
            ranges.push(sel.getRangeAt(i));
        }
        var backward = ranges.length == 1 && isSelectionBackward(sel);
        var rangeInfos = saveRanges(ranges, backward);

        // Ensure current selection is unaffected
        sel.removeAllRanges();
        if (backward) {
            selectRangeBackwards(sel, ranges[0]);
        } else {
            ranges.forEach(function (range) {
                sel.addRange(range);
            });
        }

        return {
            win: win,
            rangeInfos: rangeInfos,
            restored: false
        };
    }

    function restoreRanges(rangeInfos) {
        var ranges = [];

        // Ranges are in reverse order of appearance in the DOM. We want to restore earliest first to avoid
        // normalization affecting previously restored ranges.
        var rangeCount = rangeInfos.length;

        for (var i = rangeCount - 1; i >= 0; i--) {
            ranges[i] = restoreRange(rangeInfos[i], true);
        }

        return ranges;
    }

    function restoreSelection(savedSelection, preserveDirection) {
        if (!savedSelection.restored) {
            var rangeInfos = savedSelection.rangeInfos;
            var sel = savedSelection.win.getSelection();
            var ranges = restoreRanges(rangeInfos);
            var rangeCount = rangeInfos.length;

            sel.removeAllRanges();
            if (rangeCount == 1 && preserveDirection && selectionHasExtend && rangeInfos[0].backward) {
                selectRangeBackwards(sel, ranges[0]);
            } else {
                ranges.forEach(function (range) {
                    sel.addRange(range);
                });
            }

            savedSelection.restored = true;
        }
    }

    function removeMarkerElement(doc, markerId) {
        var markerEl = gEBI(markerId, doc);
        if (markerEl) {
            removeNode(markerEl);
        }
    }

    function removeMarkers(savedSelection) {
        savedSelection.rangeInfos.forEach(function (rangeInfo) {
            if (rangeInfo.collapsed) {
                removeMarkerElement(savedSelection.doc, rangeInfo.markerId);
            } else {
                removeMarkerElement(savedSelection.doc, rangeInfo.startMarkerId);
                removeMarkerElement(savedSelection.doc, rangeInfo.endMarkerId);
            }
        });
    }

    return {
        saveRange: saveRange,
        restoreRange: restoreRange,
        saveRanges: saveRanges,
        restoreRanges: restoreRanges,
        saveSelection: saveSelection,
        restoreSelection: restoreSelection,
        removeMarkerElement: removeMarkerElement,
        removeMarkers: removeMarkers
    };
})();

export default global_comments_utils;
